import "@/components/ui/atoms/categories/loading.css";
import { cn } from "@/utils/cn";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { memo } from "react";

/**
 * Spinner — animated loading indicator atom.
 *
 * REFERENCE PATTERN: class-selector + CSS (see
 * .claude/agent-prompts/atom-authoring-class-selector.md).
 *
 * Used directly inside button-loading
 * states, Suspense fallbacks, and any place where a sized animated indicator
 * is needed without accompanying text. Heroicons-swap codemod (Step 14)
 * replaces lucide Loader2 with Heroicons ArrowPathIcon when that runs.
 *
 * Source: features/design-system.yml components.shared (Spinner);
 *         docs/implementation-blueprint.md §8 (component contract);
 * src/components/ui/atoms/CountBadge.tsx (reference pattern proof point);
 *         2026-05-05 architecture-pivot decision (handoff §F.1 — second migration
 *         after CountBadge).
 */
interface SpinnerProps extends Omit<React.SVGProps<SVGSVGElement>, "ref"> {
    /** Pixel size token. @default "md" */
    size?: "xs" | "sm" | "md" | "lg";
}

export const Spinner = memo(
    React.forwardRef<SVGSVGElement, SpinnerProps>(function Spinner({ size = "md", className, ...props }, ref) {
        return <Loader2 ref={ref} aria-hidden="true" className={cn("dw-spinner", className)} data-size={size} data-component="Spinner" data-location="src/components/ui/atoms/Spinner.tsx" {...props} />;
    })
);
Spinner.displayName = "Spinner";
